import React from "react";
import { X } from "lucide-react";
import Button from "./Button";

/**
 * Reusable Dialog Component
 * 
 * @param {boolean} isOpen - Controls the visibility of the dialog
 * @param {function} onClose - Function to call when the close button is clicked
 * @param {string} title - Title of the dialog
 * @param {React.ReactNode} children - Content of the dialog
 * @param {boolean} hideCloseButton - Option to hide the top-right X button
 */
export default function Dialog({ isOpen, onClose, title, children, hideCloseButton = false }) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 backdrop-blur-sm p-4 md:p-0">
            {/* 
                Backdrop is part of the container (bg-black/50). 
                We do NOT attach an onClick to close it here, per requirements.
            */}

            <div className="relative w-full max-w-md max-h-full rounded-lg bg-white shadow-2xl ring-1 ring-gray-200">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 p-4 md:p-5">
                    <h3 className="text-xl font-semibold text-gray-900">
                        {title}
                    </h3>
                    {!hideCloseButton && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900"
                        >
                            <X className="w-5 h-5" />
                            <span className="sr-only">Close modal</span>
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="p-4 md:p-5 space-y-4">
                    {children}
                </div>
            </div>
        </div>
    );
}
